// SettingsPendingField — fallback renderer for the 7 logical field types
// declared with `pending_primitive: true` in sections.yaml (text, textarea,
// url, number, select, checkbox, radio).
//
// Extracted from SettingsForm to keep that file ≤ ~140 LOC (mirrors the
// FormDialog → FormDialogStepper split pattern from CHG-1233). Not exported
// from the composition barrel — internal-only.
//
// When the field-primitive registry expansion CHG ships these types
// natively, delete this file and let SettingsForm resolve them via
// @fields/registry like the canonical types.

import type { ReactNode } from "react";
import type { SettingsField } from "../../lib/settings-sections";

export interface SettingsPendingFieldProps {
  field: SettingsField;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}

const BASE_INPUT_STYLE: React.CSSProperties = {
  height: 36,
  padding: "0 12px",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  fontSize: 13,
  color: "var(--color-text)",
  width: "100%",
};

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-muted)",
  marginBottom: 4,
};

// CHG-1498 / ITER-3616 (donor-portal β.6) — static options lookup for
// the pending select + radio primitives. Lets a SettingsField declare
// `options_source: "static:<key>"` and have the pending renderer emit
// a real <select> populated from the table below (vs the previous
// "options pending" placeholder). Same key surface usable from the
// radio variant — `radio` resolves the same lookup and renders the
// options as inline <input type=radio> entries.
//
// Adding a new option set: append an entry here. Spec naming convention
// is the donor-portal / settings-sections seed key (e.g.
// "preferred_contact_channel" for the donor-portal α.6 enum).
//
// When the field-primitive registry expansion CHG ships, this table
// folds into the registry-level options resolver and this whole file
// disappears.
export const PENDING_FIELD_STATIC_OPTIONS: Record<
  string,
  ReadonlyArray<{ value: string; label: string }>
> = {
  // donor-portal α.6 / CHG-1490 — preferred inbound channel.
  preferred_contact_channel: [
    { value: "email", label: "Email" },
    { value: "sms", label: "SMS" },
    { value: "none", label: "None — don't contact me" },
  ],
};

function _resolveStaticOptions(
  optionsSource: string | undefined,
): ReadonlyArray<{ value: string; label: string }> | null {
  if (!optionsSource || !optionsSource.startsWith("static:")) return null;
  const key = optionsSource.slice("static:".length);
  return PENDING_FIELD_STATIC_OPTIONS[key] ?? null;
}

export function SettingsPendingField({ field, value, onChange, disabled }: SettingsPendingFieldProps) {
  const stringValue = value == null ? "" : typeof value === "string" ? value : String(value);
  const boolValue = !!value;
  const labelLine: ReactNode = field.label ? (
    <span style={LABEL_STYLE}>
      {field.label}
      {field.required ? <span style={{ color: "var(--color-error)" }}> *</span> : null}
    </span>
  ) : null;
  const helperLine: ReactNode = field.hint ? (
    <small style={{ color: "var(--color-text-muted)" }}>{field.hint}</small>
  ) : null;

  switch (field.field_type) {
    case "textarea":
      return (
        <label data-pending-primitive="textarea" style={{ display: "flex", flexDirection: "column" }}>
          {labelLine}
          <textarea
            name={field.field_id}
            value={stringValue}
            disabled={disabled}
            maxLength={field.max_length}
            onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
            style={{ ...BASE_INPUT_STYLE, height: 96, padding: 10, resize: "vertical" }}
          />
          {helperLine}
        </label>
      );
    case "select": {
      // CHG-2945 — page-supplied dynamic options (field.options) take
      // precedence over the static options_source lookup. See the
      // SettingsField.options doc comment in lib/settings-sections.ts.
      const staticOpts = field.options ?? _resolveStaticOptions(field.options_source);
      return (
        <label data-pending-primitive="select" style={{ display: "flex", flexDirection: "column" }}>
          {labelLine}
          <select
            name={field.field_id}
            value={stringValue}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
            style={BASE_INPUT_STYLE}
          >
            <option value="">— select —</option>
            {staticOpts
              ? staticOpts.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))
              : field.options_source ? (
                <option value="" disabled>
                  (options from {field.options_source}; resolution pending)
                </option>
              ) : null}
          </select>
          {helperLine}
        </label>
      );
    }
    case "checkbox":
      return (
        <label
          data-pending-primitive="checkbox"
          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--color-text)" }}
        >
          <input
            type="checkbox"
            name={field.field_id}
            checked={boolValue}
            disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
          />
          {field.label}
          {field.hint ? (
            <small style={{ color: "var(--color-text-muted)", marginLeft: 8 }}>{field.hint}</small>
          ) : null}
        </label>
      );
    case "radio": {
      // CHG-1498 — when options_source resolves to a static lookup,
      // render those options as radios. Falls back to the original
      // yes/no boolean radio when no options_source is declared
      // (preserves existing call-sites that haven't migrated yet).
      // CHG-2945 — page-supplied field.options take precedence (same
      // resolution order as the select case above).
      const staticOpts =
        field.options ??
        _resolveStaticOptions(field.options_source) ?? [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ];
      return (
        <fieldset data-pending-primitive="radio" style={{ border: "none", padding: 0, margin: 0 }}>
          <legend style={LABEL_STYLE}>{field.label}</legend>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {staticOpts.map((o) => (
              <label
                key={o.value}
                style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13 }}
              >
                <input
                  type="radio"
                  name={field.field_id}
                  value={o.value}
                  checked={stringValue === o.value}
                  disabled={disabled}
                  onChange={() => onChange(o.value)}
                />
                {o.label}
              </label>
            ))}
          </div>
          {helperLine}
        </fieldset>
      );
    }
    case "number":
      return (
        <label data-pending-primitive="number" style={{ display: "flex", flexDirection: "column" }}>
          {labelLine}
          <input
            type="number"
            name={field.field_id}
            value={stringValue}
            disabled={disabled}
            min={field.min}
            max={field.max}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            style={BASE_INPUT_STYLE}
          />
          {helperLine}
        </label>
      );
    case "url":
      return (
        <label data-pending-primitive="url" style={{ display: "flex", flexDirection: "column" }}>
          {labelLine}
          <input
            type="url"
            name={field.field_id}
            value={stringValue}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
            style={BASE_INPUT_STYLE}
          />
          {helperLine}
        </label>
      );
    case "text":
    default:
      return (
        <label data-pending-primitive="text" style={{ display: "flex", flexDirection: "column" }}>
          {labelLine}
          <input
            type={field.masked ? "password" : "text"}
            name={field.field_id}
            value={stringValue}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
            style={BASE_INPUT_STYLE}
          />
          {helperLine}
        </label>
      );
  }
}
